import { useEffect } from "react";
import { io, Socket } from "socket.io-client";

const WS_URL = import.meta.env.VITE_APP_INBOX_WS_URL;

// suggestion_auto_sent fires once an AI reply is linked to its suggestion,
// which is when Awaiting reply can count it as an answer.
export type InboxSocketEventName =
  | "new_message"
  | "conversation_update"
  | "suggestion_auto_sent";

const INBOX_SOCKET_EVENTS: InboxSocketEventName[] = [
  "new_message",
  "conversation_update",
  "suggestion_auto_sent"
];

export type InboxSocketEventPayload = { conversation_id?: string } | undefined;

type Listener = (
  event: InboxSocketEventName,
  payload: InboxSocketEventPayload
) => void;

const listeners = new Set<Listener>();

// Every Inbox socket republishes here, so a page can listen without opening
// its own socket when one is already up.
export const subscribeInboxSocketEvents = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const publishInboxSocketEvent = (
  event: InboxSocketEventName,
  payload?: InboxSocketEventPayload
) => {
  listeners.forEach((listener) => listener(event, payload));
};

const connectEventsSocket = (accessToken: string) => {
  const socket = io(`${WS_URL}/portal/inbox`, {
    auth: { token: accessToken },
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 30000,
    reconnectionAttempts: Infinity
  });

  INBOX_SOCKET_EVENTS.forEach((event) =>
    socket.on(event, (payload?: InboxSocketEventPayload) =>
      publishInboxSocketEvent(event, payload)
    )
  );

  return socket;
};

const sharedSockets = new Map<string, { socket: Socket; holders: number }>();

const acquireEventsSocket = (accessToken: string) => {
  const shared = sharedSockets.get(accessToken) ?? {
    socket: connectEventsSocket(accessToken),
    holders: 0
  };
  shared.holders += 1;
  sharedSockets.set(accessToken, shared);

  return () => {
    shared.holders -= 1;
    if (shared.holders > 0) return;
    shared.socket.disconnect();
    sharedSockets.delete(accessToken);
  };
};

export const useInboxEventsSocket = ({
  accessToken,
  enabled
}: {
  accessToken: string | undefined;
  enabled: boolean;
}) => {
  useEffect(() => {
    if (!accessToken || !enabled || !WS_URL) return;

    return acquireEventsSocket(accessToken);
  }, [accessToken, enabled]);
};
